import axios from "axios";
import store from "../store";
import { setCredentials, clearCredentials } from "../store/authSlice";

/* ── Axios instance ── */
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:5001",
  withCredentials: true, // send httpOnly cookies
});

// Attach JWT token + ngrok header
api.interceptors.request.use((config) => {
  const token = store.getState().auth.token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.headers["ngrok-skip-browser-warning"] = "true";
  return config;
});

// Handle 401 - try refresh once, then fail
let isRefreshing = false;
let failedQueue = [];

function processQueue(error, token) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
}

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = err.config;

    // Don't retry refresh/login/register calls or already-retried requests
    if (
      err.response?.status !== 401 ||
      original._retry ||
      original.url?.includes("/api/auth/")
    ) {
      return Promise.reject(err);
    }

    if (isRefreshing) {
      // Queue this request until refresh completes
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        original.headers.Authorization = `Bearer ${token}`;
        return api(original);
      });
    }

    original._retry = true;
    isRefreshing = true;

    try {
      const { data } = await axios.post(
        `${api.defaults.baseURL}/api/auth/refresh`,
        {},
        { withCredentials: true },
      );
      const { token, user } = data.data;
      store.dispatch(setCredentials({ token, user }));
      processQueue(null, token);
      original.headers.Authorization = `Bearer ${token}`;
      return api(original);
    } catch (refreshErr) {
      processQueue(refreshErr, null);
      store.dispatch(clearCredentials());
      if (window.location.pathname.startsWith("/admin")) {
        window.location.href = "/admin/login";
      }
      return Promise.reject(refreshErr);
    } finally {
      isRefreshing = false;
    }
  },
);

export default api;
